/**
 * @fileoverview Loans & Advances — Interest Rate-wise Loan Portfolio page.
 * @description Ported from the InsightBox Banking design system's own
 * `LoansAdvances/InterestRateWise.tsx`. The reference identifies "high-rate" slabs by matching two
 * hardcoded slab keys (`"13.5-15"`/`"16-18"`) — kept here as a named, commented constant rather than
 * an inline magic-string check, per the Banking module plan's "clean up demo-fragile inline
 * calculations, don't reproduce verbatim" note.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useCurrentSession } from '@/features/auth'
import {
  BranchFilterSelect,
  CategoryColumnChart,
  FIVE_YEAR_ID,
  FyFilterSelect,
  LastUpdatedLabel,
  branchLabel,
  formatCr,
  loanRateSlabs,
  resolveBankingAccessScope,
  resolveFy,
  useBankingData,
} from '@/features/banking'
import BankingKpiStrip from '@/shared/components/molecules/BankingKpiStrip'
import BusinessSummaryPanel from '@/shared/components/molecules/BusinessSummaryPanel'
import ChartCard from '@/shared/components/molecules/ChartCard'
import PageHeader from '@/shared/components/molecules/PageHeader'
import { categoricalPalette } from '@/shared/constants/chart-theme.constants'

import type { CategoryColumnItem, FyFilter } from '@/features/banking'
import type { BankingKpiStripItem } from '@/shared/components/molecules/BankingKpiStrip'

// The two rate slabs above 13.50% — the mock dataset's own slab boundaries, not a computed
// threshold, so this stays a named lookup rather than parsing the boundary out of the slab key.
const HIGH_RATE_SLAB_KEYS = ['13.5-15', '16-18']

/**
 * @component
 */
function BankingLoansInterestRateWisePage() {
  const { t } = useTranslation()
  const dataQuery = useBankingData()
  const [fyFilter, setFyFilter] = useState<FyFilter>(FIVE_YEAR_ID)
  const bankingScope = resolveBankingAccessScope(useCurrentSession().data?.bankingScope)
  const [branchId, setBranchId] = useState(bankingScope.defaultBranchId)

  const header = (
    <PageHeader
      kicker={t('banking.loansInterestRateWise.kicker')}
      title={t('banking.loansInterestRateWise.title')}
      description={t('banking.loansInterestRateWise.description')}
      filters={
        <>
          <FyFilterSelect value={fyFilter} onChange={setFyFilter} />
          <BranchFilterSelect value={branchId} onChange={setBranchId} scope={bankingScope} />
          {dataQuery.data && <LastUpdatedLabel value={dataQuery.data.meta.lastUpdated} />}
        </>
      }
    />
  )

  if (dataQuery.isPending) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <BankingKpiStrip
          loading
          items={[
            { label: '', value: '', highlight: true },
            { label: '', value: '' },
            { label: '', value: '' },
            { label: '', value: '' },
          ]}
        />
        <ChartCard title="" loading>
          <div />
        </ChartCard>
      </div>
    )
  }

  if (dataQuery.isError) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <p className="text-sm text-danger">{t('banking.loadError')}</p>
      </div>
    )
  }

  const data = dataQuery.data
  const fy = resolveFy(fyFilter)
  const slabs = loanRateSlabs(data, fy, branchId)
  const total = slabs.reduce((sum, s) => sum + s.amountCr, 0)
  const sorted = [...slabs].sort((a, b) => b.sharePct - a.sharePct)
  const top = sorted[0]!
  const top2Share = sorted[0]!.sharePct + sorted[1]!.sharePct
  const highRateShare = slabs
    .filter((s) => HIGH_RATE_SLAB_KEYS.includes(s.key))
    .reduce((sum, s) => sum + s.sharePct, 0)
  const colors = categoricalPalette(slabs.length)

  const columnItems: CategoryColumnItem[] = slabs.map((s, i) => ({
    key: s.key,
    label: s.label,
    value: s.amountCr,
    topLabel: `${formatCr(s.amountCr)} / ${s.sharePct.toFixed(2)}%`,
    color: colors[i]!,
  }))

  const kpis: BankingKpiStripItem[] = [
    {
      label: t('banking.loansInterestRateWise.kpi.totalOutstanding'),
      value: formatCr(total),
      sublabel: t('banking.loansInterestRateWise.kpi.acrossAllProducts'),
      highlight: true,
    },
    {
      label: t('banking.loansInterestRateWise.kpi.largestBandShare'),
      value: `${top.sharePct.toFixed(2)}%`,
      sublabel: top.label,
    },
    {
      label: t('banking.loansInterestRateWise.kpi.top2BandsShare'),
      value: `${top2Share.toFixed(2)}%`,
      sublabel: `${sorted[0]!.label} + ${sorted[1]!.label}`,
    },
    {
      label: t('banking.loansInterestRateWise.kpi.highRateShare'),
      value: `${highRateShare.toFixed(2)}%`,
      sublabel: t('banking.loansInterestRateWise.kpi.above1350'),
    },
  ]

  return (
    <div className="flex flex-col gap-4">
      {header}
      <BankingKpiStrip items={kpis} />

      <ChartCard
        fill
        title={t('banking.loansInterestRateWise.chartTitle')}
        subtitle={t('banking.loansInterestRateWise.chartSubtitle', {
          branch: branchLabel(branchId),
          fy,
        })}
      >
        <div className="relative min-h-0 flex-1">
          <CategoryColumnChart items={columnItems} />
        </div>
      </ChartCard>

      <BusinessSummaryPanel
        title={t('banking.businessSummary')}
        lines={[
          t('banking.loansInterestRateWise.summary.line1', {
            share: top.sharePct.toFixed(2),
            label: top.label,
          }),
          t('banking.loansInterestRateWise.summary.line2', { share: highRateShare.toFixed(2) }),
        ]}
      />
    </div>
  )
}

export default BankingLoansInterestRateWisePage
